import { MessageSquare } from 'lucide-react';

interface AssistantConversation {
  name: string;
  todayConversations: number;
  totalConversations: number;
}

interface ConversationMetricsCardProps {
  assistants: AssistantConversation[];
  loading?: boolean;
}

export function ConversationMetricsCard({ assistants, loading = false }: ConversationMetricsCardProps) {
  if (loading) {
    return (
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-3 bg-gray-200 rounded w-full" />
          <div className="h-3 bg-gray-200 rounded w-5/6" />
        </div>
      </div>
    );
  }

  const totalToday = assistants.reduce((sum, a) => sum + a.todayConversations, 0);
  const totalAll = assistants.reduce((sum, a) => sum + a.totalConversations, 0);

  return (
    <div className="bg-white border border-border rounded-xl p-5 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="bg-purple-50 p-2 rounded-lg">
              <MessageSquare className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="font-semibold text-foreground">Conversation Activity</h3>
          </div>
          <div className="flex items-baseline gap-4 mt-3">
            <div>
              <p className="text-xs text-muted-foreground">Today</p>
              <p className="text-2xl font-semibold text-purple-600">{totalToday}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-2xl font-semibold text-foreground">{totalAll}</p>
            </div>
          </div>
        </div>
      </div>

      {assistants.length > 0 && (
        <>
          {/* Divider */}
          <div className="border-t border-gray-100 my-4" />

          {/* Assistant Breakdown */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              By Assistant
            </p>
            {assistants.map((assistant, index) => (
              <div
                key={index}
                className="grid grid-cols-[1fr_auto] gap-4 py-2 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors items-center"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{assistant.name}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Today</p>
                    <p className="text-sm font-semibold text-foreground">{assistant.todayConversations}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-sm font-semibold text-foreground">{assistant.totalConversations}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
